import { getSocialUrl, type SearchIdentity } from 'lib/seo/socialUrl'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { getSocialImageUrl, resolveSocialImage, type SocialImageCandidate, type SocialVertical } from 'lib/seo/socialImage'
import config from 'constants/config'
import { resolveLocalizedConfigValue } from 'constants/config/resolveLocalizedConfigValue'
import { removeRegionCode } from 'lib/string/regionUtils'
import { getLanguageOptions, getLanguageAlternateLinks, getDefaultLocaleForRegion } from 'i18n/util/locale'
import { useIntl } from 'react-intl'
import { LabRemoteConfigKeys } from 'constants/lab'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useContext } from 'react'
import EvergreenPageSeoContext from 'contexts/EvergreenPageSeoContext'

interface OpenGraphMetaData {
  description?: string
  image?: string
  imageAlt?: string
  // siteName?: string
  title?: string
  // url?: string
}

type TwitterCardType = 'summary' | 'summary_large_image'/* | 'app' | 'player' */
type TwitterUsername = '@Lux_Escapes' | undefined

interface TwitterMetaData {
  card?: TwitterCardType
  description?: string
  image?: string
  imageAlt?: string
  title?: string
  username?: TwitterUsername
}

interface Props {
  searchIdentity?: SearchIdentity
  socialImage?: SocialImageCandidate
  destinationImage?: SocialImageCandidate
  vertical?: SocialVertical
  restricted?: boolean
  canonical?: string
  customerPortalURL: string
  description?: string
  imageId?: string
  imageUrl?: string
  imageAlt?: string
  noIndex?: boolean
  originalCanonicalURL: string
  regionCode: string
  title?: string
  twitter?: TwitterMetaData
  defer?: boolean
  languagesRemoteConfig?: App.LabRemoteConfigResult
}

export function HeadData(props: Props) {
  const location = useLocation()
  const {
    description,
    twitter,
    imageAlt,
    imageId,
    imageUrl,
    title,
    customerPortalURL,
    originalCanonicalURL,
    noIndex,
    canonical,
    regionCode,
    defer,
    languagesRemoteConfig,
  } = props

  const intl = useIntl()
  const evergreenSeo = useContext(EvergreenPageSeoContext)
  const metaTitle = evergreenSeo?.title || title || resolveLocalizedConfigValue(intl, config.head.defaultTitle)
  const metaDescription = description || resolveLocalizedConfigValue(intl, config.head.defaultDescription)
  const image = resolveSocialImage({
    image: props.socialImage ?? (imageUrl ? { url: imageUrl, alt: imageAlt } : imageId ? { id: imageId, alt: imageAlt } : undefined),
    destination: props.destinationImage,
    vertical: props.vertical,
    restricted: props.restricted,
    alt: imageAlt || metaTitle,
  })
  const metaImage = image?.url
  const metaImageAlt = image?.alt
  // The app-level default must not leave dimensions behind when a page uses an external image.
  const hasPageImage = image?.source !== 'brand'

  const sourcePath = IS_SSR ? originalCanonicalURL : location.pathname
  const sourceLocation = UrlLocation.fromUrl(sourcePath)
  const { languageOptions } = getLanguageOptions(languagesRemoteConfig)

  const redirect = (!sourceLocation?.locale ? null :
      sourceLocation.getRedirectToLocale({
        targetLocale: sourceLocation.locale,
        languageOptions,
        allowCanonical: true,
        canonicalLocale: getDefaultLocaleForRegion(regionCode, languageOptions),
      })
  ) ?? undefined
  const canonicalLocalePath = UrlLocation.fromUrl(sourcePath, redirect).path
  const path = removeRegionCode(canonicalLocalePath, regionCode)
  const alternatePath = canonical ?? path
  const altFirstSegment = alternatePath.split('/')[1]
  const bareAlternatePath = altFirstSegment && languageOptions.some(opt => opt.slug === altFirstSegment || opt.locale === altFirstSegment)
    ? ['', ...alternatePath.split('/').slice(2)].join('/') || '/'
    : alternatePath
  const languageAlternateLinks = getLanguageAlternateLinks({
    customerPortalURL,
    path: bareAlternatePath,
    languageOptions,
  })

  const canonicalUrl = `${customerPortalURL}/${regionCode.toLowerCase()}${canonical ?? path}`

  const isSearchPage = /(?:\/search(?:\/|$)|\/flights-search-results(?:-multi-city)?(?:\/|$))/.test(path)
  const socialUrl = !canonical && isSearchPage && props.searchIdentity
    ? getSocialUrl(canonicalUrl, location.search, props.searchIdentity)
    : canonicalUrl

  const ogMeta: OpenGraphMetaData = {
    title: metaTitle,
    image: metaImage,
    description: metaDescription,
    imageAlt: metaImageAlt,
  }

  const twitterMeta: TwitterMetaData = {
    card: 'summary_large_image' as TwitterCardType,
    username: config.BRAND === 'luxuryescapes' ? '@Lux_Escapes' : undefined as TwitterUsername,
    title: twitter?.title || metaTitle,
    image: twitter?.image ? getSocialImageUrl(twitter.image) : metaImage,
    description: twitter?.description || metaDescription,
    imageAlt: twitter?.imageAlt || metaImageAlt,
  }

  // Small trick to avoid Helmet adding alternate tags that are not canonical in accommodation and bedbank offer pages.
  const pauseAddingAlternateTags = !canonical && (path.includes('/offer/') || path.includes('/partner/'))

  return (
    <Helmet defer={defer}>
      <title>{metaTitle}</title>
      <meta name="title" content={metaTitle}></meta>
      <meta name="description" content={metaDescription}></meta>
      <link
        rel="canonical"
        href={canonicalUrl}
      />
      {noIndex &&
        <meta name="robots" content="noindex, follow" />}
      <meta name="robots" content="max-image-preview:large" />
      {!pauseAddingAlternateTags && languageAlternateLinks.map(link => (
        <link
          key={link.hrefLang}
          rel="alternate"
          href={link.href}
          hrefLang={link.hrefLang}
        />
      ))}
      <link rel="alternate" href={`${customerPortalURL}/au${bareAlternatePath}`} hrefLang="x-default" />

      <meta property="og:locale" content={intl.locale.replace(/-/g, '_')}></meta>
      <meta property="og:type" content="website"></meta>
      <meta property="og:title" content={ogMeta.title}></meta>
      <meta property="og:description" content={ogMeta.description}></meta>
      {ogMeta.image && <meta property="og:image" content={ogMeta.image} itemProp="image"></meta>}
      {ogMeta.image && <meta property="og:image:alt" content={ogMeta.imageAlt}></meta>}
      {hasPageImage && image?.width && <meta property="og:image:width" content={image.width.toString()}></meta>}
      {hasPageImage && image?.height && <meta property="og:image:height" content={image.height.toString()}></meta>}
      {hasPageImage && image?.mimeType && <meta property="og:image:type" content={image.mimeType}></meta>}
      <meta property="og:url" content={socialUrl}></meta>
      <meta property="og:site_name" content={config.title}></meta>

      <meta name="twitter:title" content={twitterMeta.title}></meta>
      <meta name="twitter:description" content={twitterMeta.description}></meta>
      <meta name="twitter:card" content={twitterMeta.card}></meta>
      {twitterMeta.image && <meta name="twitter:image" content={twitterMeta.image}></meta>}
      {twitterMeta.image && <meta name="twitter:image:alt" content={twitterMeta.imageAlt}></meta>}
      {twitterMeta.username && <meta name="twitter:site" content={twitterMeta.username}></meta>}
    </Helmet>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    customerPortalURL: state.system.customerPortalURL,
    originalCanonicalURL: state.system.originalCanonicalURL,
    languagesRemoteConfig: state.system.features.remoteConfigFlags[LabRemoteConfigKeys.languages],
  }
}

export default connect(mapStateToProps)(HeadData)
